import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Image, Row, Spinner, Table } from "react-bootstrap";
import LiveExam from "../../Services/liveexam.services";
import CustomModal from "./Modal";
import ApprovedImage from "../../assets/icons/teacher.png";
import toast from "react-hot-toast";

interface IEndExam {
  show: any;
  setShow: any;
  onSuccess?: any;
}

const EndExamConfirmation = ({ show, setShow, onSuccess }: IEndExam) => {
  const [summary, setSummary] = useState<any>(null);
  const [submitAllowed, setSubmitAllowed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const [summaryRes, submitRes] = await Promise.all([
        LiveExam.getSubmissionSummary(),
        LiveExam.checkIfSubmitAllowed().catch(() => null),
      ]);

      if (summaryRes.status === 200) {
        setSummary(summaryRes.data.data);
      }
      if (submitRes && submitRes.status === 200) {
        setSubmitAllowed(true);
      } else {
        setSubmitAllowed(false);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const onReviewSent = async () => {
    try {
      const res = await LiveExam.updateReviewStatus();
      if (res.status) {
        toast.success("Sent for Review");
        setShow(false);
        window.location.reload();
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Failed to send for review";
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (show) fetchSummary();
  }, [show]);

  return (
    <CustomModal
      displayModal={show}
      onClose={() => setShow(false)}
      size="xl"
      removeClose={true}
      modalBody={
        loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <div className="text-muted mt-2">Loading summary...</div>
          </div>
        ) : !summary ? (
          <Alert variant="danger">Failed to load submission summary.</Alert>
        ) : (
          <>
            <h5 className="fw-bold">End Exam Confirmation</h5>
            <Alert variant="warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Are you sure you want to end your exam!
            </Alert>

            <div className="mb-2">
              <Row>
                <Col>
                  <div className="fw-bold">
                    <span className="text-secondary">Total Questions: </span>
                    {summary.totalQuestions}
                  </div>
                </Col>
                <Col>
                  <div className="fw-bold">
                    <span className="text-secondary">Answered: </span>
                    {summary.answered + summary.markedAndAnswered}
                  </div>
                </Col>
              </Row>
              <div className="fw-bold">
                <span className="text-secondary">Not Answered: </span>
                {summary.notAnswered}
              </div>
              <div className="fw-bold mt-1">
                <span className="text-secondary">Minimum Required: </span>
                {summary.minimumRequired}
              </div>
            </div>

            <hr />
            <div className="mt-3">
              <h5 className="fw-bold">Section Wise Summary</h5>
              <Table responsive striped bordered>
                <thead>
                  <tr>
                    <th className="text-center">Name</th>
                    <th className="text-center">Total Questions</th>
                    <th className="text-center">Answered</th>
                    <th className="text-center">Not Answered</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.sectionSummary?.map((section: any, i: number) => (
                    <tr key={section.sectionId || i}>
                      <td className="fw-bold">{section.sectionName}</td>
                      <td className="text-center text-primary fw-bold">
                        {section.totalQuestions}
                      </td>
                      <td className="text-center text-success fw-bold">
                        {section.answered + section.markedAndAnswered}
                      </td>
                      <td className="text-center fw-bold text-danger">
                        {section.notAnswered}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

                {(!submitAllowed && summary.reviewAllowed) ? (
              (!summary.studentSubmitForReview) ? (
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <span className="fw-bold text-success h5">
                    You can send your responses to invigilator for review.
                  </span>
                  <Button
                    variant="success"
                    onClick={onReviewSent}
                    className="ms-2"
                  >
                    Send for Review
                  </Button>
                </div>
              ) : (
                <div>
                  <Alert variant="info">
                    Already Submitted for review: Awaited from Instructor.
                  </Alert>
                </div>
              )
            ) : (
              !submitAllowed &&
              <div>
                <Alert variant="danger">
                  You have not attempted minimum required questions for review.
                  You need at least {summary.minimumRequired} answered questions.
                </Alert>
              </div>
            )}

            {submitAllowed ? (
              <div className="d-flex justify-content-center align-items-center mb-3">
                <Image src={ApprovedImage} style={{ height: "11rem" }} />
                <span className="fw-bold text-success h3">
                  Your responses are approved!
                  <br />
                  <span className="text-secondary" style={{ fontSize: "1rem" }}>
                    You can submit the exam now.
                  </span>
                </span>
              </div>
            ) : (
              <div>
                <Alert variant="danger">
                  You are not allowed to submit the exam till invigilator
                  approves your responses.
                </Alert>
              </div>
            )}

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={() => setShow(false)}
                className="me-2"
              >
                Go, back
              </Button>
              <Button
                variant="danger"
                onClick={onSuccess}
                disabled={!submitAllowed}
              >
                {submitAllowed ? "Submit" : "Approval Awaited"}
              </Button>
            </div>
          </>
        )
      }
    />
  );
};

export default EndExamConfirmation;
